import { useCallback, useEffect, useMemo, useState } from 'react'

import { getMessageThreads } from '../_data/messages'
import { useAuth } from './use-auth'
import {
  MessagesUnreadContext,
  MessagesUnreadContextValue,
} from './use-messages-unread'

const POLL_INTERVAL_MS = 30000

interface MessagesUnreadProviderProps {
  children: React.ReactNode
}

/**
 * Tracks the renter's total unread message count across all property threads.
 * Polls the thread list (which carries per-thread unread counts) on an
 * interval so the header badge stays fresh, and exposes a manual `refresh`
 * the inbox calls right after marking a thread read.
 *
 * @param {MessagesUnreadProviderProps} props
 * @returns {JSX.Element}
 */
const MessagesUnreadProvider = ({
  children,
}: MessagesUnreadProviderProps): JSX.Element => {
  const { quickPassUser } = useAuth()
  const [totalUnread, setTotalUnread] = useState<number>(0)

  const refresh = useCallback(async (): Promise<void> => {
    if (quickPassUser === null) {
      setTotalUnread(0)
      return
    }
    const threads = await getMessageThreads(quickPassUser.id)
    const total = threads.reduce(
      (sum, thread) => sum + (thread.unreadCount ?? 0),
      0,
    )
    setTotalUnread(total)
  }, [quickPassUser])

  useEffect(() => {
    if (quickPassUser === null) {
      setTotalUnread(0)
      return
    }
    void refresh()
    const handle = setInterval(() => {
      void refresh()
    }, POLL_INTERVAL_MS)
    return () => {
      clearInterval(handle)
    }
  }, [quickPassUser, refresh])

  const value = useMemo<MessagesUnreadContextValue>(
    () => ({ totalUnread, refresh }),
    [totalUnread, refresh],
  )

  return (
    <MessagesUnreadContext.Provider value={value}>
      {children}
    </MessagesUnreadContext.Provider>
  )
}

export { MessagesUnreadProvider }
